import { useId, useRef, useState } from 'react';
import { useT } from '../i18n';
import { ACCEPTED_IMAGE_ACCEPT_ATTR, type SelectedFile } from '../domain/types';
import './PhotoInput.css';

interface PhotoInputProps {
  name: string;
  label: string;
  help: string;
  /** The "JPG, PNG, ... max N" line. */
  meta?: string;
  optional?: boolean;
  /** 15 for photos, 1 for the plan. */
  max: number;
  cta: string;
  /** Shown as the CTA when a single-slot input already holds a file. */
  ctaReplace?: string;
  dropHint: string;
  files: SelectedFile[];
  error?: string | undefined;
  /** Type/size check for one file. Returns a message or null. */
  validate: (file: File) => string | null;
  /** Compress + build a SelectedFile (with preview). Async. */
  process: (file: File) => Promise<SelectedFile>;
  onAdd: (entries: SelectedFile[]) => void;
  onRemove: (id: string) => void;
  /** Set or clear the inline reject message (bad type, oversize, too many). */
  onReject: (message: string | null) => void;
}

/** Romanian file sizes: comma decimal, and a non-breaking space before the unit. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    return `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(bytes / 1024)} kB`;
  }
  return `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
}

/**
 * The image upload control, used for both the interior photos (a grid, up to
 * fifteen) and the apartment plan (a single tile). It validates, compresses and
 * previews on the client; the compression is what keeps a fifteen-photo upload
 * from a phone under control.
 *
 * Tiles appear one at a time as each image finishes processing, so the owner
 * sees progress rather than a frozen button while a batch is prepared.
 */
export function PhotoInput({
  name,
  label,
  help,
  meta,
  optional,
  max,
  cta,
  ctaReplace,
  dropHint,
  files,
  error,
  validate,
  process,
  onAdd,
  onRemove,
  onReject,
}: PhotoInputProps) {
  const t = useT();
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);

  const single = max === 1;
  const atMax = files.length >= max;

  const handleFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onReject(null);
    if (inputRef.current) inputRef.current.value = '';

    let incoming = Array.from(list);
    if (single) incoming = incoming.slice(0, 1);

    // Validate shape (type/size) first, keeping the first rejection message.
    const valid: File[] = [];
    let rejectMsg: string | null = null;
    for (const file of incoming) {
      const fileError = validate(file);
      if (fileError) rejectMsg = fileError;
      else valid.push(file);
    }

    // Enforce the count for the multi grid.
    const remaining = single ? 1 : Math.max(0, max - files.length);
    let toProcess = valid;
    if (!single && valid.length > remaining) {
      toProcess = valid.slice(0, remaining);
      rejectMsg = t.validation.tooManyPhotos(max);
    }

    if (rejectMsg) onReject(rejectMsg);
    if (toProcess.length === 0) return;

    setProcessing(true);
    try {
      // Sequential, not parallel: fifteen web-worker compressions at once can
      // exhaust memory on a phone. One at a time, appending as each completes.
      for (const file of toProcess) {
        const entry = await process(file);
        onAdd([entry]);
      }
    } finally {
      setProcessing(false);
    }
  };

  const showAddZone = !atMax || processing;

  return (
    <div className="photo-input" data-field={name}>
      <div className="field__label" id={`${id}-label`}>
        {label}
        {optional && <span className="field__optional">{t.step2.planOptional}</span>}
        {!single && (
          <span className="photo-input__count" aria-hidden="true">
            {t.step2.photoCount(files.length, max)}
          </span>
        )}
      </div>

      <p className="field__help" id={helpId}>
        {help}
        {meta && <span className="photo-input__meta">{meta}</span>}
      </p>

      {(files.length > 0 || processing) && (
        <ul className={single ? 'photo-grid photo-grid--single' : 'photo-grid'}>
          {files.map((entry) => (
            <PhotoTile
              key={entry.id}
              entry={entry}
              removeLabel={t.step2.fileRemoveNamed(entry.file.name)}
              altLabel={t.step2.photoAlt(entry.file.name)}
              noPreviewLabel={t.step2.photoNoPreview}
              size={formatBytes(entry.file.size)}
              onRemove={() => onRemove(entry.id)}
            />
          ))}
          {processing && (
            <li className="photo-tile photo-tile--processing" aria-hidden="true">
              <span className="spinner" />
            </li>
          )}
        </ul>
      )}

      {processing && (
        <p className="photo-input__processing" role="status" aria-live="polite">
          {t.step2.photosProcessing}
        </p>
      )}

      {showAddZone && (
        <div
          className={dragging ? 'file-input__drop file-input__drop--active' : 'file-input__drop'}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void handleFiles(e.dataTransfer.files);
          }}
        >
          <input
            ref={inputRef}
            id={id}
            className="visually-hidden file-input__native"
            type="file"
            accept={ACCEPTED_IMAGE_ACCEPT_ATTR}
            multiple={!single}
            aria-labelledby={`${id}-label`}
            aria-describedby={[error ? errorId : null, helpId].filter(Boolean).join(' ')}
            aria-invalid={Boolean(error)}
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <label className="btn file-input__cta" htmlFor={id}>
            {single && files.length > 0 && ctaReplace ? ctaReplace : cta}
          </label>
          <span className="file-input__drop-hint">{dropHint}</span>
        </div>
      )}

      {error && (
        <p className="field__error" id={errorId}>
          <span className="visually-hidden">{t.validation.fieldError}</span>
          <span aria-hidden="true">↳</span>
          {error}
        </p>
      )}
    </div>
  );
}

interface PhotoTileProps {
  entry: SelectedFile;
  removeLabel: string;
  altLabel: string;
  noPreviewLabel: string;
  size: string;
  onRemove: () => void;
}

function PhotoTile({ entry, removeLabel, altLabel, noPreviewLabel, size, onRemove }: PhotoTileProps) {
  // HEIC previews cannot decode outside Safari; the <img> then errors and we
  // swap in a placeholder rather than a broken image.
  const [failed, setFailed] = useState(false);
  const showImage = entry.previewUrl !== null && !failed;

  return (
    <li className="photo-tile">
      <div className="photo-tile__frame">
        {showImage ? (
          <img
            className="photo-tile__img"
            src={entry.previewUrl ?? undefined}
            alt={altLabel}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        ) : (
          <span className="photo-tile__placeholder" title={entry.file.name}>
            {noPreviewLabel}
          </span>
        )}
        <button type="button" className="photo-tile__remove" onClick={onRemove} aria-label={removeLabel}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <span className="photo-tile__size">{size}</span>
    </li>
  );
}
